/**
 * 报告复核 · 列表。
 *
 * 三条口径（与 apps/api 的 report.routes.ts 一致，界面不自行改写）：
 *   1. **待复核数量来自接口**（`summary/pending` 按可见范围分组计数）——
 *      界面上任何一个数字都不能写死，否则角色或范围一变，界面就在说假话；
 *   2. 复核状态显示**中文**：ready 待复核 / reviewing 复核中 / reviewed 已复核 / failed 生成失败。
 *      直接把英文枚举摆在专家面前，会被读成系统字段而不是「这份报告到哪一步了」；
 *   3. 列表与汇总都按守护关系过滤。二者用两个 `useLoad` 分别取数，**互不掩盖失败**：
 *      列表 403 就整屏照实说 403；只要汇总是 403，就在数字位置写明「无权限查看」，
 *      绝不显示 0 —— 「看不到」与「是 0」在复核排期上是完全不同的两个结论。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportCaliber } from '@hew3/shared';
import { adminApi } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { Badge, Card, Kpi, PageHead } from '../components/ui.tsx';
import { EmptyBox, ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';
import {
  MISSING_TEXT,
  formatConfidence,
  formatDateTime,
  formatText,
  generatedByBadge,
  reportField,
  reviewDecisionLabel,
  reviewStatusLabel,
  reviewStatusTone,
  scopeLabel,
} from '../lib/format.ts';

/** 状态筛选：取值即接口的 status 枚举，标签即复核状态的中文名（同一张表，避免两处漂移）。 */
const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: '全部状态' },
  { value: 'ready', label: '待复核' },
  { value: 'reviewing', label: '复核中' },
  { value: 'reviewed', label: '已复核' },
  { value: 'failed', label: '生成失败' },
];

/** 类型筛选：名称取自共享口径 reportCaliber，不在前端另写一份文案。 */
const TYPE_FILTERS: { value: string; label: string }[] = [
  { value: '', label: '全部类型' },
  ...reportCaliber.types.map((type) => ({ value: type.key, label: type.name })),
];

export function ReportsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');

  const list = useLoad((signal) => adminApi.reports(status, type, 50, signal));
  const summary = useLoad((signal) => adminApi.pendingReports(signal));

  if (list.loading) return <LoadingBox text="正在加载报告列表…" />;
  if (list.error) return <ErrorBox message={errorText(list.error)} onRetry={list.reload} />;
  if (!list.data) return <ErrorBox message="报告列表为空" onRetry={list.reload} />;

  const items = list.data.items;
  const byReviewStatus = summary.data?.byReviewStatus ?? [];
  const countOf = (key: string): number => byReviewStatus.find((row) => row.reviewStatus === key)?.count ?? 0;

  return (
    <div>
      <PageHead
        title="报告复核"
        sub="「AI 结论须经人类专家确认」不是一句话：报告落库即为复核中，只有复核通过才会变成已复核。顺序由服务端按生成时间倒序给出。"
        actions={<span className="badge ghost">可见范围：{scopeLabel(list.data.scope)}</span>}
      />

      <Card
        title="待复核队列"
        sub="计数来自 summary/pending，按您的可见范围分组"
        actions={
          summary.loading ? (
            <span className="text-tiny muted">正在加载计数…</span>
          ) : summary.error ? (
            <span className="text-tiny muted">计数读取失败</span>
          ) : (
            <span className="text-tiny muted">
              可见成员：
              {summary.data?.visibleMemberCount === null || summary.data?.visibleMemberCount === undefined
                ? '不限范围'
                : `${summary.data.visibleMemberCount} 名`}
            </span>
          )
        }
      >
        {summary.error ? (
          <div className="state-box error">
            <div className="st-ico">⚠</div>
            <div className="st-title">{errorText(summary.error)}</div>
            <div>待复核数量在当前权限下不可见 —— 这里显示的不是 0，而是「没有权限读到这个数字」。</div>
            <button type="button" className="btn sm" onClick={summary.reload}>
              重试
            </button>
          </div>
        ) : (
          <div className="grid4">
            <Kpi label="待复核" value={countOf('pending')} tone="warn" delta="尚未有专家结论" />
            <Kpi label="复核通过" value={countOf('approved')} tone="e" delta="review_status = approved" />
            <Kpi label="需修订" value={countOf('revise')} tone="danger" delta="专家判定需要修订后再发布" />
            <Kpi label="生成失败" value={countOf('failed')} delta="未产出正文，需要重新生成" />
          </div>
        )}
      </Card>

      <Card tight>
        <div className="chip-row">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.value || 'all-status'}
              type="button"
              className={`chip${status === filter.value ? ' on' : ''}`}
              onClick={() => setStatus(filter.value)}
            >
              {filter.label}
            </button>
          ))}
          <span className="muted" style={{ alignSelf: 'center', padding: '0 6px' }}>
            |
          </span>
          {TYPE_FILTERS.map((filter) => (
            <button
              key={filter.value || 'all-type'}
              type="button"
              className={`chip${type === filter.value ? ' on' : ''}`}
              onClick={() => setType(filter.value)}
            >
              {filter.label}
            </button>
          ))}
          <span className="text-tiny muted" style={{ alignSelf: 'center', marginLeft: 8 }}>
            当前筛选命中 {items.length} 条（单次最多 50 条）
          </span>
        </div>
      </Card>

      <Card tight>
        {items.length === 0 && (
          <EmptyBox
            title="当前筛选下没有报告"
            desc="这可能只是「这个筛选条件没有命中」——不是「平台没有报告」。换一个状态或类型再看一次，或清空筛选。"
          />
        )}

        {items.length > 0 && (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>报告编号</th>
                  <th>类型</th>
                  <th>标题</th>
                  <th>成员</th>
                  <th>生成时间</th>
                  <th>置信度</th>
                  <th>复核状态</th>
                  <th style={{ textAlign: 'right' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="mono-num">{item.reportNo}</td>
                    <td>{reportField(item.typeName)}</td>
                    <td>
                      <b>{item.title}</b>
                      <div className="muted text-tiny" title="报告产出方式（如实标识）">
                        {generatedByBadge(item.generatedBy)}
                      </div>
                    </td>
                    <td>
                      {reportField(item.memberName)}
                      <span className="muted mono-num"> · {reportField(item.memberNo)}</span>
                    </td>
                    <td className="mono-num">{formatDateTime(item.generatedAt)}</td>
                    <td className="mono-num">{formatConfidence(item.confidence)}</td>
                    <td>
                      <Badge tone={reviewStatusTone(item.status)}>{reviewStatusLabel(item.status)}</Badge>
                      {item.reviewStatus !== undefined && (
                        <div className="muted text-tiny">结论：{reviewDecisionLabel(item.reviewStatus)}</div>
                      )}
                      {item.reviewer ? (
                        <div className="muted text-tiny">
                          {formatText(item.reviewer.name)} · {formatDateTime(item.reviewer.at)}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button type="button" className="link" onClick={() => navigate(`/reports/${item.id}`)}>
                        查看详情
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="note-src">
        <b>状态口径</b>
        <br />
        待复核 = 已生成、尚无人给出结论；复核中 = 正在被判读；已复核 = 专家已给出通过或需修订的结论；
        生成失败 = 正文未产出，需要重新生成。列表里的「结论」列是复核结论（{MISSING_TEXT} 表示还没有人复核），
        与上面的报告状态不是同一个字段。
      </div>

      <div className="note-src">
        <b>权限与可见范围</b>
        <br />
        读取报告需要 <span className="mono-num">report:read</span>，提交复核需要{' '}
        <span className="mono-num">report:review</span>；两者都还要通过守护关系校验 ——
        范围外成员的报告即使有权限点也会被服务端拒绝。本页只读，复核动作在报告详情页；
        没有 <span className="mono-num">report:review</span> 的角色打开详情页时不会看到复核区。
      </div>
    </div>
  );
}
